/**
 * LockedPlan — blurred list of locked courses with an overlay.
 * Used in State 2 (showState2Buttons=true) and State 3 (showUpgradeButton=true).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardCourse } from '../types';
import { ROUTES } from '../../../utils/constants';

interface LockedPlanProps {
  courses: DashboardCourse[];
  /** State 3: show a single amber "Upgrade →" button */
  showUpgradeButton?: boolean;
  /** State 2: show updated copy + two-button set (Upgrade + free course link) */
  showState2Buttons?: boolean;
}

export const LockedPlan: React.FC<LockedPlanProps> = ({
  courses,
  showUpgradeButton = false,
  showState2Buttons = false,
}) => {
  return (
    <div className="relative rounded-2xl overflow-hidden">
      {/* Blurred course rows */}
      <div style={{ filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none' }}>
        {courses.map((course) => (
          <div
            key={course.id}
            className="flex items-center gap-4 p-4 mb-2 rounded-2xl bg-white"
            style={{ border: '1.5px solid #e8e8ee' }}
          >
            <div className="text-2xl">{course.emoji}</div>
            <div>
              <p className="font-montserrat font-bold text-sm text-neutral-800">
                {course.title}
              </p>
              <p className="font-open-sans text-xs text-neutral-500">
                {course.durationMin} min · {course.category}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Lock overlay */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl text-center px-6 py-8"
        style={{
          backdropFilter: 'blur(2px)',
          background: 'rgba(247,248,255,0.82)',
        }}
      >
        <div className="text-4xl mb-3">🔒</div>
        <p className="font-montserrat font-bold text-base text-neutral-800 mb-1">
          {courses.length} more course{courses.length !== 1 ? 's' : ''} in your plan
        </p>
        <p className="font-open-sans text-sm text-neutral-600 mb-4 max-w-xs leading-relaxed">
          {showState2Buttons
            ? 'Upgrade to Premium to unlock your full plan — or complete a free course first to try before you buy.'
            : showUpgradeButton
            ? 'Upgrade to access the rest of your personalised plan.'
            : 'Complete a free course first, then unlock your full plan with Premium.'}
        </p>

        {showState2Buttons && (
          <div className="flex flex-col items-center gap-2">
            <Link
              to={ROUTES.pricing}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full font-montserrat font-bold text-sm text-white transition-transform duration-200 hover:-translate-y-px"
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                boxShadow: '0 4px 14px rgba(245,158,11,0.30)',
              }}
            >
              Upgrade to Premium →
            </Link>
            <a
              href="#featured-course"
              className="font-open-sans text-xs font-semibold transition-opacity duration-150 hover:opacity-70"
              style={{ color: '#9ca3af' }}
            >
              Start a free course first
            </a>
          </div>
        )}

        {showUpgradeButton && !showState2Buttons && (
          <Link
            to={ROUTES.pricing}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full font-montserrat font-bold text-sm text-white transition-transform duration-200 hover:-translate-y-px"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #f97316)',
              boxShadow: '0 4px 14px rgba(245,158,11,0.30)',
            }}
          >
            Upgrade →
          </Link>
        )}
      </div>
    </div>
  );
};
